import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSun, FaCloud, FaCloudRain, FaSnowflake, FaBolt, FaSearch, FaTint, FaWind, FaThermometerHalf, FaSmog } from 'react-icons/fa'; // Added new icons
import './App.css';

function App() {
  const [city, setCity] = useState('Kolkata');
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState({ hourly: [], daily: [] });
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const apiKey = process.env.REACT_APP_WEATHERAPI_KEY;
  const sliderRef = useRef(null);

  // Fetch weather and forecast
  const fetchWeatherAndForecast = async (query) => {
    try {
      const response = await axios.get(
        `https://api.weatherapi.com/v1/forecast.json?key=${apiKey}&q=${query}&days=7&aqi=yes`
      );
      console.log('WeatherAPI Data:', response.data);
      const data = response.data;

      setWeather({
        name: data.location.name,
        country: data.location.country,
        main: {
          temp: data.current.temp_c,
          humidity: data.current.humidity,
          feels_like: data.current.feelslike_c,
        },
        weather: [{ main: data.current.condition.text }],
        wind: { speed: data.current.wind_kph / 3.6 },
        air_quality: data.current.air_quality,
      });

      setForecast({
        hourly: data.forecast.forecastday[0].hour.map((hour) => ({
          dt: hour.time_epoch,
          temp: hour.temp_c,
          weather: [{ main: hour.condition.text }],
        })),
        daily: data.forecast.forecastday.map((day) => ({
          dt: day.date_epoch,
          temp: { day: day.day.avgtemp_c },
          weather: [{ main: day.day.condition.text }],
        })),
      });
      setError('');
    } catch (err) {
      setError('City not found or API error.');
      console.error('WeatherAPI fetch error:', err.response?.data || err.message);
      setWeather(null);
      setForecast({ hourly: [], daily: [] });
    }
  };

  // Auto-scroll hourly forecast to current time
  const scrollToCurrentHour = () => {
    if (sliderRef.current && forecast.hourly.length > 0) {
      const now = new Date();
      const currentHour = now.getHours();
      const hourIndex = forecast.hourly.findIndex(
        (hour) => new Date(hour.dt * 1000).getHours() === currentHour
      );
      if (hourIndex !== -1) {
        const scrollPosition = hourIndex * 120;
        sliderRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (city.trim()) fetchWeatherAndForecast(city);
  };

  // Get user location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchWeatherAndForecast(`${latitude},${longitude}`);
        },
        () => fetchWeatherAndForecast('Kolkata')
      );
    } else {
      fetchWeatherAndForecast('Kolkata');
    }
  };

  // Update time and auto-scroll hourly forecast
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      scrollToCurrentHour();
    }, 60000);
    fetchWeatherAndForecast('Kolkata');
    getUserLocation();
    return () => clearInterval(timer);
  }, [forecast.hourly]);

  // Weather icon mapping
  const getWeatherIcon = (condition) => {
    const cond = condition?.toLowerCase();
    if (cond.includes('sunny') || cond.includes('clear')) return <FaSun />;
    if (cond.includes('cloud')) return <FaCloud />;
    if (cond.includes('rain') || cond.includes('shower')) return <FaCloudRain />;
    if (cond.includes('snow')) return <FaSnowflake />;
    if (cond.includes('thunder')) return <FaBolt />;
    return <FaCloud />;
  };

  return (
    <div className="app-container">
      <div className="hero-section">
        <h1 className="text-center text-white mb-4">Weather Insights</h1>
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="input-group w-50 mx-auto glass-input">
            <input
              type="text"
              className="form-control"
              placeholder="Enter city name"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">
              <FaSearch />
            </button>
          </div>
        </form>
        {error && <p className="text-danger text-center">{error}</p>}
      </div>

      <div className="container mt-5">
        <div className="clock text-center mb-5 glass-card">
          <h2>{currentTime.toLocaleTimeString()}</h2>
          <p>{currentTime.toLocaleDateString()}</p>
        </div>

        {weather ? (
          <>
            <div className="weather-card glass-card mx-auto mb-5">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="card-title">{weather.name}, {weather.country}</h2>
                    <p className="weather-desc">{weather.weather[0].main}</p>
                  </div>
                  <div className="weather-icon">
                    {getWeatherIcon(weather.weather[0].main)}
                    <span className="temp">{Math.round(weather.main.temp)}°C</span>
                  </div>
                </div>
                <div className="details-grid mt-4">
                  <p><FaTint /> Humidity: {weather.main.humidity}%</p>
                  <p><FaWind /> Wind: {weather.wind.speed.toFixed(1)} m/s</p>
                  <p><FaThermometerHalf /> Feels Like: {Math.round(weather.main.feels_like)}°C</p>
                  {weather.air_quality && (
                    <p><FaSmog /> AQI: {Math.round(weather.air_quality['us-epa-index'])}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Hourly Forecast Slider */}
            <h3 className="text-center mb-4 neon-text">Hourly Forecast</h3>
            {forecast.hourly.length > 0 ? (
              <div className="slider-container glass-card mb-5" ref={sliderRef}>
                {forecast.hourly.map((hour, index) => (
                  <div key={index} className="forecast-card">
                    <div className="card-body text-center">
                      <p>{new Date(hour.dt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      <div className="forecast-icon">{getWeatherIcon(hour.weather[0].main)}</div>
                      <p>{Math.round(hour.temp)}°C</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center">No hourly forecast available.</p>
            )}

            {/* Horizontal Daily Forecast */}
            <h3 className="text-center mb-4 neon-text">7-Day Forecast</h3>
            {forecast.daily.length > 0 ? (
              <div className="slider-container glass-card mb-5">
                {forecast.daily.map((day, index) => (
                  <div key={index} className="forecast-card">
                    <div className="card-body text-center">
                      <p>{new Date(day.dt * 1000).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                      <div className="forecast-icon">{getWeatherIcon(day.weather[0].main)}</div>
                      <p>{Math.round(day.temp.day)}°C</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center">No daily forecast available.</p>
            )}
          </>
        ) : (
          <p className="text-center mt-5">Loading weather data...</p>
        )}
      </div>
    </div>
  );
}

export default App;